import { NgIf } from '@angular/common';
import { Component, EventEmitter, inject, OnInit, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatError, MatFormField } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { b64toBlob } from '@app/api/core/utils/file.util';
import { formatMobilNum } from '@app/api/core/utils/format.util';
import { getKontaktPreferHaupt, personHasKontakt } from '@app/api/core/utils/helper/person-helper';
import { GsmWarnError } from '@app/shared/gsm/gsm-ui-lib-ng-shared/src/lib/gsm-error';
import { GsmErrorService } from '@app/shared/gsm/gsm-ui-lib-ng-shared/src/lib/gsm-error.service';
import { GsmService } from '@app/shared/gsm/gsm-ui-lib-ng-shared/src/lib/gsm.service';
import { LowLevelApi } from '@app/shared/services/state/low-level-api.service';
import { DialogStatus } from '@app/shared/services/ui/dialog/dialog.types';
import { Benutzer, EsignContainer, EsignElement } from '@gsm/entities';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ObjektTyp } from '@o30/entities';
import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { DialogService } from '../../../services/ui/dialog/dialog.service';
import { MessageService } from '../../../services/ui/message/message.service';
import { NoDataComponent } from '../../no-data/no-data.component';

export interface DialogData {
  schadenNummer: string;
}

@UntilDestroy()
@Component({
  selector:    'app-gsm-esign-dialog',
  templateUrl: './gsm-esign-dialog.component.html',
  styleUrls:   ['./gsm-esign-dialog.component.scss'],
  standalone:  true,
  imports: [
    MatDialogTitle,
    NgIf,
    MatDialogContent,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatFormField,
    MatInput,
    FormsModule,
    MatError,
    MatIconButton,
    MatTooltip,
    MatIcon,
    MatButton,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    NoDataComponent,
    MatProgressSpinner,
    MatDialogActions,
    MatCheckbox
  ]
})

export class GsmEsignDialogComponent implements OnInit {
  private dialogRef = inject<MatDialogRef<GsmEsignDialogComponent>>(MatDialogRef);
  private dialogService = inject(DialogService);
  private gsmService = inject(GsmService);
  private llapi = inject(LowLevelApi);
  data = inject<DialogData>(MAT_DIALOG_DATA);
  private messageService = inject(MessageService);
  private gsmErrorService = inject(GsmErrorService);

  private _esignContainer: EsignContainer;

  displayedColumnsStepOne: string[] = [
    'typ-one',
    'nachname-one',
    'vorname-one',
    'email-one',
    'mobil-one',
    'bearbeiten',
    'drucken'
  ];
  displayedColumnsStepTwo: string[] = [
    'typ',
    'nachname',
    'vorname',
    'email',
    'mobil',
    'fernunterschrift',
    'bekommtkopie',
    'status',
    'aktion'
  ];
  stepOne = true;
  isloading: boolean;
  isErstellenLoading: boolean;
  isVerwerfenLoading: boolean;
  isAktualisierenLoading: boolean;
  isFertigLoading: boolean;
  benutzer: Benutzer;
  newErstellenIndex: number;
  newVerwerfenIndex: number;
  phonePattern = '^\\+[0-9]{10}|^\\+[0-9]{10}\\(0\\)|^\\(\\+[0-9]{10}\\)\\(0\\)';
  emailPattern = '[\\w-]+@([\\w-]+\\.)+[\\w-]+';

  @ViewChild('ccCheckbox') ccCheckbox;
  @ViewChild('emailInput') emailInput;
  @ViewChild('handyInput') handyInput;

  @Output() dialogClose = new EventEmitter();

  ngOnInit(): void {
    this.isloading = true;
    this.benutzer = this.llapi.getValue('gsm.benutzer');
    this.refreshEsignContainer()
      .catch(err => this.gsmErrorService.catchError(err));
  }

  get esignContainer(): EsignContainer {
    return this._esignContainer;
  }

  set esignContainer(value: EsignContainer) {
    if (!value) {
      this._esignContainer = null;
      this.elements = [];
    }

    this._esignContainer = value;
  }

  get elements(): EsignElement[] {
    return this.esignContainer?.elements || [];
  }

  set elements(value: EsignElement[]) {
    const elements = value || [];
    this.esignContainer = {
      ...this.esignContainer,
      elements
    };
  }

  private async refreshEsignContainer(): Promise<EsignContainer> {
    this.isloading = true;
    const container$ = this.gsmService.getEsignContainer(this.benutzer, this.data.schadenNummer)
      .pipe(
        this.gsmErrorService.handleErrorAndReturnNull()
      );
    const container = await firstValueFrom(container$);

    this.esignContainer = container;
    this.isloading = false;

    return container;
  }

  public getEsignDocument(documentLabel: string, documentLastName: string, documentType: string) {
    this.gsmService.getEsignDocument(this.benutzer, this.data.schadenNummer, documentType)
      .subscribe({
        next:  base64Value => {
          this.openPdf(base64Value, documentLastName, documentLabel);
        },
        error: err => this.gsmErrorService.catchError(err)
      });
  }

  private openEsignWindow(esignElement?: EsignElement): Window {
    if (!esignElement?.url) {
      return null;
    }
    return this.openWindow(esignElement.url);
  }

  private openWindow(url?: string): Window {
    if (!url) {
      return null;
    }

    const win = window.open(url, '_blank', 'noopener,noreferrer');
    if (win) {
      win.opener = null;
    }
    return win;
  }

  private openPdf(base64: string, documentLastName: string, documentLabel: string) {
    const pdfWindow = window.open('');
    if (pdfWindow) {
      pdfWindow.location.href = URL.createObjectURL(b64toBlob(base64, 'application/pdf'));
    }
  }

  public openEditKundeDialog(esignElement: EsignElement): void {
    if (esignElement.kundenId) {
      this.dialogService.displayPersonErfassenDialog({
          objektTyp:  ObjektTyp.KUNDE,
          objId:      esignElement.kundenId,
          kfa:        false,
          skipPolice: true
        })
        .afterClosed()
        .pipe(
          take(1))
        .subscribe(_dialogErfassenResult => {
            if (_dialogErfassenResult?.data && _dialogErfassenResult?.statusId === DialogStatus.SAVE_SUCCESS) {
              const person = _dialogErfassenResult.data;
              esignElement.vorname = person.vorname.value;
              esignElement.nachname = person.name.value;
              if (personHasKontakt(person, 'email')) {
                esignElement.email = getKontaktPreferHaupt(person, 'email');
                this.emailInput.nativeElement.value = getKontaktPreferHaupt(person, 'email');
              }
              if (personHasKontakt(person, 'mobil')) {
                esignElement.handy = formatMobilNum(getKontaktPreferHaupt(person, 'mobil'));
                this.handyInput.nativeElement.value = formatMobilNum(getKontaktPreferHaupt(person, 'mobil'));
              }
            }
          }
        );
    }
  }

  onWeiterClick() {
    this.stepOne = !this.stepOne;
  }

  onAbbrechenClick() {
    this.dialogRef.close();
  }

  async onAktualisierenClick() {
    // we might have unsaved Data in this step.
    const previousElements = this.elements.reduce((map, el) => map[el.type] = el, {} as {
      [key: string]: EsignElement
    });

    this.isAktualisierenLoading = true;

    const container = await this.refreshEsignContainer();

    // restore unsaved personal data if we have a new container.
    if (container) {
      this.elements = this.elements.map((elem => {
        const previousElement = previousElements[elem.type];

        return {
          ...elem,
          vorname:  (previousElement && previousElement.vorname) || elem.vorname || '',
          nachname: (previousElement && previousElement.nachname) || elem.nachname || '',
          email:    (previousElement && previousElement.email) || elem.email || '',
          handy:    (previousElement && previousElement.handy) || elem.handy || ''
        };
      }));
    }

    // stop loading
    this.isAktualisierenLoading = false;
  }

  async onErstellenClick(esignElement: EsignElement, index) {
    if (this.newErstellenIndex !== index) {
      this.newErstellenIndex = index;
    }

    this.isErstellenLoading = true;

    this.gsmService.sendEsign(this.benutzer, {
        ...this.esignContainer,
        elements: [esignElement]
      })
      .pipe(
        this.gsmErrorService.handleErrorAndReturnNull()
      )
      .subscribe(value => {
        this.isErstellenLoading = false;

        if (value) {
          this.updateElement(value.elements[index], index);
          this.openEsignWindow(value.elements[index]);
        }
      });
  }

  updateElement(element: EsignElement, index: number) {
    // insert new element
    this.elements = [
      ...this.elements.slice(0, index),
      element,
      ...this.elements.slice(index + 1)
    ];
  }

  onUnterschreibenClick(esignElement: EsignElement) {
    if (esignElement.url) {
      this.openWindow(`${esignElement.url}`);
    } else {
      throw new GsmWarnError('Zu diesem Dokument ist derzeit keine elektronische Unterschrift möglich. Versuchen Sie bitte über die Buttons "Verwerfen" und "Erstellen" ein neues elektronisches Dokument zu erstellen.');
    }
  }

  onVerwerfenClick(esignElement: EsignElement, index) {
    if (this.newVerwerfenIndex !== index) {
      this.newVerwerfenIndex = index;
    }
    this.isVerwerfenLoading = true;

    this.gsmService.verwerfenEsign(this.benutzer, {
        ...this.esignContainer,
        elements: [esignElement]
      })
      .pipe(
        this.gsmErrorService.handleErrorAndReturnNull()
      )
      .subscribe(value => {
        this.isVerwerfenLoading = false;
        if (value) {
          this.updateElement(value.elements[index], index);
        }
      });
  }

  onFertigClick() {
    this.dialogRef.close();
  }

  getInputPlaceholder(inputData): string {
    if (inputData) {
      return inputData;
    } else {
      return null;
    }
  }

  updateEmail(event, i) {
    if (this.esignContainer.elements[i]) {
      this.esignContainer.elements[i].email = event.target.value;
    }
  }

  updateHandy(event, i) {
    if (this.esignContainer.elements[i]) {
      this.esignContainer.elements[i].handy = event.target.value;
    }
  }

  onFernunterschriftChange(esignElement: EsignElement) {
    // if email or mobile number (handy) is missing show dialog and return
    if (!esignElement.email || !esignElement.handy) {
      const id = 'gsm-esign-dialog-handler';
      const headline = 'Keine Kontaktdaten vorhanden';
      const text = 'Der Fernunterschriftenprozess benötigt alle Kontaktdaten.';
      this.messageService.showDialog({
          id,
          headline,
          text
        })
        .afterClosed()
        .pipe(
          take(1),
          untilDestroyed(this)
        )
        .subscribe(() => esignElement.fernunterschrift = false);

      return;
    }

    // disable Fernunterschrift
    if (!esignElement.fernunterschrift) {
      return;
    }
    this.isloading = true;
    esignElement.status = 'A';

    this.gsmService.sendEsign(this.benutzer, {
        ...this.esignContainer,
        elements: [esignElement]
      })
      .pipe(
        this.gsmErrorService.handleErrorAndReturnNull()
      )
      .subscribe(value => {
        this.esignContainer = value;
        this.isloading = false;
      });
  }

  onCCClick(esignElement: EsignElement) {
    if (!esignElement.email) {
      const id = 'gsm-esign-dialog-handler';
      const headline = 'Keine Email Adresse vorhanden';
      const text = 'Die Zusendung einer Kopie benötigt eine Email Adresse.';
      this.messageService.showDialog({
          id,
          headline,
          text
        })
        .afterClosed()
        .pipe(
          take(1),
          untilDestroyed(this)
        )
        .subscribe(() => esignElement.cc = false);
    }
  }

  getPageTwoElements() {
    if (this.elements) {
      return this.elements.filter(element => element.type !== 'VOLLMEDI');
    }
  }
}
